import { useState } from 'react';
import { Navigate } from 'react-router';
import type { UserRole } from '@br/protocol';
import { errorMessage } from '../api/client';
import {
  useCreateUserInvite,
  useUpdateUser,
  useUsers,
  type UserInviteCreated,
  type UserListItem,
} from '../api/queries';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  CopyButton,
  EmptyState,
  Field,
  FullPageSpinner,
  Input,
  PageHeader,
  Select,
} from '../components/ui';
import { formatDateTime } from '../lib/format';
import { ROLE_LABEL } from '../lib/labels';
import { usePageTitle } from '../lib/title';

export function AdminUsersPage() {
  usePageTitle('사용자');
  const { user: me, isAdmin } = useAuth();
  const toast = useToast();
  const users = useUsers();
  const invite = useCreateUserInvite();
  const update = useUpdateUser();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('member');
  const [created, setCreated] = useState<UserInviteCreated | null>(null);
  const [toggling, setToggling] = useState<UserListItem | null>(null);
  const fail = (err: unknown) => toast.error(errorMessage(err));

  if (!isAdmin) return <Navigate to="/profiles" replace />;

  return (
    <div className="space-y-5">
      <PageHeader title="사용자" description="운영자 계정은 초대 링크로만 만들 수 있습니다." />
      <div className="grid gap-5 lg:grid-cols-3">
        <Card title="운영자" className="lg:col-span-2" padded={false}>
          {users.isPending && <FullPageSpinner />}
          {users.isError && <Alert level="error">{errorMessage(users.error)}</Alert>}
          {users.data && users.data.length === 0 && <EmptyState title="사용자가 없습니다" />}
          {users.data && users.data.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-canvas text-left text-xs text-ink-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">이름</th>
                  <th className="px-4 py-2 font-medium">이메일</th>
                  <th className="px-4 py-2 font-medium">역할</th>
                  <th className="px-4 py-2 font-medium">가입</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {users.data.map((u) => {
                  const self = u.id === me.id;
                  return (
                    <tr key={u.id} className="border-t border-line">
                      <td className="px-4 py-2.5 font-medium text-ink">
                        {u.name}
                        {self && <span className="ml-1 text-xs text-ink-muted">(나)</span>}
                        {u.disabled && (
                          <Badge tone="muted" className="ml-2">
                            비활성
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-ink-muted">{u.email}</td>
                      <td className="px-4 py-2.5">
                        <Select
                          aria-label="역할"
                          className="h-8 w-auto text-xs"
                          value={u.role}
                          disabled={self}
                          onChange={(e) =>
                            update.mutate(
                              { id: u.id, role: e.target.value as UserRole },
                              { onError: fail },
                            )
                          }
                        >
                          <option value="member">{ROLE_LABEL.member}</option>
                          <option value="admin">{ROLE_LABEL.admin}</option>
                        </Select>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-ink-muted">
                        {formatDateTime(u.createdAt)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {!self && (
                          <Button
                            size="sm"
                            variant={u.disabled ? 'secondary' : 'danger'}
                            onClick={() => setToggling(u)}
                          >
                            {u.disabled ? '활성화' : '비활성화'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="운영자 초대">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              invite.mutate(
                { email: email.trim() || undefined, role },
                { onSuccess: (r) => setCreated(r), onError: fail },
              );
            }}
          >
            <Field label="이메일" hint="비우면 누구나 이 링크로 가입할 수 있습니다">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="선택"
              />
            </Field>
            <Field label="역할">
              <Select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
                <option value="member">{ROLE_LABEL.member}</option>
                <option value="admin">{ROLE_LABEL.admin}</option>
              </Select>
            </Field>
            <Button type="submit" variant="primary" loading={invite.isPending}>
              초대 링크 만들기
            </Button>
          </form>
          {created && (
            <div className="mt-4 space-y-2 rounded-lg border border-line bg-canvas p-3 text-xs">
              <p className="font-medium text-ink">
                초대 링크 ({ROLE_LABEL[created.role]}
                {created.email ? `, ${created.email}` : ''})
              </p>
              <code className="block break-all rounded bg-surface p-2">{created.url}</code>
              <div className="flex items-center justify-between">
                <span className="text-ink-muted">만료 {formatDateTime(created.expiresAt)}</span>
                <CopyButton text={created.url} label="링크 복사" />
              </div>
            </div>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={toggling !== null}
        title={toggling?.disabled ? '계정을 활성화할까요?' : '계정을 비활성화할까요?'}
        message={
          toggling?.disabled
            ? `${toggling.name}님이 다시 로그인할 수 있게 됩니다.`
            : `${toggling?.name ?? ''}님의 모든 세션이 종료되고 로그인할 수 없게 됩니다.`
        }
        confirmLabel={toggling?.disabled ? '활성화' : '비활성화'}
        danger={!toggling?.disabled}
        loading={update.isPending}
        onCancel={() => setToggling(null)}
        onConfirm={() =>
          toggling &&
          update.mutate(
            { id: toggling.id, disabled: !toggling.disabled },
            { onSuccess: () => setToggling(null), onError: fail },
          )
        }
      />
    </div>
  );
}
